export const SCHOOL_PAYMENT_OPTION_TYPES = {
  SCHOOL_EMPLOYER_SPONSORED: "SCHOOL_EMPLOYER_SPONSORED",
  SCHOOL_FINANCIAL_AID: "SCHOOL_FINANCIAL_AID",
  SCHOOL_NON_PLATFORM: "SCHOOL_NON_PLATFORM",
  SCHOOL_PAY_IN_FULL: "SCHOOL_PAY_IN_FULL",
  SCHOOL_PAY_PER_LEAD: "SCHOOL_PAY_PER_LEAD",
  SCHOOL_PAYMENT_PLAN: "SCHOOL_PAYMENT_PLAN",
} as const;

export const SPONSOR_PAYMENT_OPTION_TYPES = {
  EMPLOYER_SPONSORED_SCHOOL: "EMPLOYER_SPONSORED_SCHOOL",
} as const;

export type SchoolPaymentOptionType =
  (typeof SCHOOL_PAYMENT_OPTION_TYPES)[keyof typeof SCHOOL_PAYMENT_OPTION_TYPES];
export type SponsorPaymentOptionType =
  (typeof SPONSOR_PAYMENT_OPTION_TYPES)[keyof typeof SPONSOR_PAYMENT_OPTION_TYPES];

/**
 * PaymentOption configs
 */

export type SchoolEmployerSponsoredPaymentOptionConfig = PaymentOptionsWithChargesToSponsor &
  SchoolPaymentOptionsWithRevenueShare &
  SchoolPaymentOptionsWithProcessingFee & {
    /**
     * Additional fee to the student for selecting a sponsored payment option.
     */
    studentSurcharge: number;
  };

export type SchoolNonPlatformPaymentOptionConfig = SchoolPaymentOptionsWithRevenueShare &
  SchoolPaymentOptionsWithCustomDescription;

export type SchoolFinancialAidPaymentOptionConfig = Record<string, never>;

export type SchoolPayInFullPaymentOptionConfig = PaymentOptionsWithFirstCharge &
  SchoolPaymentOptionsWithProcessingFee &
  SchoolPaymentOptionsWithRevenueShare &
  PaymentOptionsWithDiscount &
  PaymentOptionsWithNonRefundablePortion;

export type SchoolPaymentPlanPaymentOptionConfig = PaymentOptionsWithFirstCharge &
  SchoolPaymentOptionsWithProcessingFee &
  SchoolPaymentOptionsWithRevenueShare &
  PaymentOptionsWithDiscount &
  PaymentOptionsWithNonRefundablePortion & {
    /**
     * Amount of the first payment. For example, if the program costs $500 and
     * you enter $100, then the first payment is $100 and remaining payments
     * add up to $400. In general, we will try to maximize the first payment
     * size up to this amount: if the total cost is less than this amount, the
     * total cost will be used and there will only be 1 payment. If this is
     * left blank, then the first payment will be the same amount as all other
     * payments.
     */
    firstChargeMaximum?: number;
    /**
     * Number of payments after the first. For example, if you enter 2, then
     * there will be 3 total payments due.
     */
    subsequentChargesCount: number;
  };

export type SchoolPayPerLeadPaymentOptionConfig = {
  pplFlatRate: number | undefined;
  pplPercentageRate: number | undefined;
};

export type EmployerSponsoredSchoolPaymentOptionConfig = {
  sponsorSurchargePercent: number;
  sponsorSurchargeFlatRate: number;
};

/**
 * PaymentOption config default values
 * (Overlapping keys on various PaymentOption types are configured separately
 * since they may have different defaults in different contexts.)
 */

export const schoolEmployerSponsoredOptionDefaults: SchoolEmployerSponsoredPaymentOptionConfig = {
  revenueSharePercentage: 20,
  revenueShareFlatRate: 0,
  processingFeePercentage: 3,
  studentSurcharge: 0,
};

export const schoolNonPlatformPaymentOptionDefaults: SchoolNonPlatformPaymentOptionConfig = {
  chargesToSchoolNetDaysDue: 30,
  revenueSharePercentage: 0,
  revenueShareFlatRate: 0,
  description: "",
};

export const schoolFinancialAidPaymentOptionDefaults: SchoolFinancialAidPaymentOptionConfig = {};

export const schoolPayInFullPaymentOptionDefaults: SchoolPayInFullPaymentOptionConfig = {
  chargesToSchoolNetDaysDue: 30,
  firstChargeOffsetDays: -2,
  processingFeePercentage: 0,
  revenueSharePercentage: 0,
  revenueShareFlatRate: 0,
  discountAmount: 0,
  nonRefundableAmount: 0,
};

export const schoolPaymentPlanPaymentOptionDefaults: SchoolPaymentPlanPaymentOptionConfig = {
  chargesToSchoolNetDaysDue: 30,
  firstChargeOffsetDays: -2,
  firstChargeMaximum: 10000,
  processingFeePercentage: 0,
  revenueSharePercentage: 0,
  revenueShareFlatRate: 0,
  subsequentChargesCount: 1,
  discountAmount: 0,
  nonRefundableAmount: 0,
};

export const schoolPayPerLeadPaymentOptionDefaults: SchoolPayPerLeadPaymentOptionConfig = {
  pplFlatRate: 0,
  pplPercentageRate: 0,
};

export const employerSponsoredSchoolPaymentOptionDefaults: EmployerSponsoredSchoolPaymentOptionConfig =
  {
    sponsorSurchargePercent: 10,
    sponsorSurchargeFlatRate: 0,
  };

// Formik has to initialize every input. Because they are rendered depending on
// which plan is chosen, we need to have a list of properties with empty values
// for when the type is switched. These mirror paymentOptionConfigDetails but
// the values are overwritten by the plan defaults
export const paymentOptionConfigEmptyFormValues: CombinedPaymentOptionConfig = {
  chargesToSchoolNetDaysDue: 0,
  chargesToSponsorNetDaysDue: 0,
  firstChargeMaximum: 0,
  firstChargeOffsetDays: 0,
  processingFeePercentage: 0,
  revenueSharePercentage: 0,
  revenueShareFlatRate: 0,
  subsequentChargesCount: 0,
  discountAmount: 0,
  nonRefundableAmount: 0,
  studentSurcharge: 0,
  sponsorSurchargeFlatRate: 0,
  sponsorSurchargePercent: 0,
  pplFlatRate: 0,
  pplPercentageRate: 0,
  description: "",
};

/**
 * Explanations of PaymentOption config values
 */
export type configFormInputType = "dollarAmountInCents" | "boolean" | "number" | "date" | "string";

type PaymentOptionConfigDetail = {
  name: string;
  description: string;
  formInputType: configFormInputType;
  readOnly?: boolean;
  customInputProps?: Record<string, unknown>;
};

export const paymentOptionConfigDetails: {
  [Property in keyof CombinedPaymentOptionConfig]: PaymentOptionConfigDetail;
} = {
  chargesToSchoolNetDaysDue: {
    name: "Revenue net days (due from school after invoicing)",
    description:
      'Number of days after invoicing ("net days") that a payment from the ' +
      "school to Dreambound is due. This only applies to separate charges " +
      "for revenue/processing fees, not to revenue that is deducted directly " +
      "from payouts. If this is not set, the charges will not have a due " +
      "date (i.e. they will never be overdue).",
    formInputType: "number",
  },
  chargesToSponsorNetDaysDue: {
    name: "School payment net days (due to school after invoicing)",
    description:
      'Number of days after invoicing ("net days") that a payment from ' +
      "Dreambound to the school is due; e.g. for a Dreambound Agreement " +
      "funded program. If this is not set, the charges will not have a due " +
      "date (i.e. they will never be overdue).",
    formInputType: "number",
  },
  firstChargeMaximum: {
    name: "Amount of first payment",
    description:
      "Amount of the first payment. For example, if the program costs $500 " +
      "and you enter $100, then the first payment is $100 and remaining " +
      "payments add up to $400. In general, we will try to maximize the " +
      "first payment size up to this amount: if the total cost is less than " +
      "this amount, the total cost will be used and there will only be 1 " +
      "payment. If this is left blank, then the first payment will be the " +
      "same amount as all other payments.",
    formInputType: "dollarAmountInCents",
  },
  firstChargeOffsetDays: {
    name: "Student is first charged",
    description:
      "Number of days before (negative number) or after (positive number) the " +
      "start of the program that the first payment will be due.",
    formInputType: "number",
  },
  processingFeePercentage: {
    name: "IGNORED: Dreambound processing fee percentage",
    description:
      "NO PROCESSING FEES ARE CURRENTLY CHARGED. / ORIGINAL: The percentage " +
      "of what the customer pays that goes to Dreambound as a processing " +
      "fee. Enter as a whole percent; for example, for 3% enter '3'.",
    formInputType: "number",
    readOnly: true,
  },
  revenueSharePercentage: {
    name: "Dreambound revenue share percentage",
    description:
      "The revenue share, or percentage of what the customer pays that goes " +
      "to Dreambound as a referral fee. Enter as a whole percent; for " +
      "example, for 20% enter '20'.",
    formInputType: "number",
    readOnly: true,
  },
  revenueShareFlatRate: {
    name: "Dreambound revenue share flat rate",
    description:
      "The revenue share as a flat rate. If both this and Dreambound " +
      "revenue share percentage are defined, the total revenue share is the " +
      "dollar amount of their sum.",
    formInputType: "dollarAmountInCents",
    readOnly: true,
  },
  subsequentChargesCount: {
    name: "Number of subsequent payments (payment plans)",
    description:
      "Number of payments after the first. For example, if you enter 2, then " +
      "there will be 3 total payments due.",
    formInputType: "number",
  },
  discountAmount: {
    name: "Discount for selecting this payment option",
    description:
      "Discount offered to customers if they select this payment option. " +
      "For example, to offer a discount when paying in full. Displays as a " +
      "negative line item in invoice.",
    formInputType: "dollarAmountInCents",
  },
  nonRefundableAmount: {
    name: "Non-refundable application fee",
    description:
      "Portion (not an additional charge) of the first payment that will " +
      "not be refunded to the student. The non-refundable portion can " +
      "never exceed the first payment amount.",
    formInputType: "dollarAmountInCents",
  },
  studentSurcharge: {
    name: "Student surcharge",
    description: "Surcharge to customer for selecting a sponsored payment option.",
    formInputType: "dollarAmountInCents",
  },
  sponsorSurchargePercent: {
    name: "Sponsor surcharge (percent)",
    description:
      "Percent of total program cost that a sponsor will pay Dreambound when they sponsor a student. Takes precedent over flat rate.",
    formInputType: "number",
  },
  sponsorSurchargeFlatRate: {
    name: "Sponsor surcharge (flat rate)",
    description: "Flat cost that a sponsor will pay Dreambound when they sponsor a student",
    formInputType: "dollarAmountInCents",
  },
  pplFlatRate: {
    name: "Pay-per-lead fee (flat rate)",
    description: "Flat cost that a school will pay Dreambound for a lead",
    formInputType: "dollarAmountInCents",
    readOnly: true,
  },
  pplPercentageRate: {
    name: "Pay-per-lead fee (percentage)",
    description: "Percentage of program cost that a school will pay Dreambound for a lead",
    formInputType: "number",
    readOnly: true,
  },
  description: {
    name: "Custom description",
    description:
      "A custom label that will be displayed on our search page if this payment option is enabled (50 character max).",
    formInputType: "string",
    customInputProps: {
      maxLength: 50,
    },
  },
};

/**
 * PaymentOption config building blocks
 */
type PaymentOptionsWithFirstCharge = {
  /**
   * Number of days before (negative number) or after (positive number) the
   * start of the program that the first payment will be due.
   */
  firstChargeOffsetDays: number;
};

export type PaymentOptionsWithChargesToSchool = {
  /**
   * Number of days after invoicing ("net days") that a payment from the school
   * to Dreambound is due. This only applies to separate charges for revenue/
   * processing fees, not to revenue that is deducted directly from payouts. If
   * this is not set, the charges will not have a due date (i.e. they will never
   * be overdue).
   */
  chargesToSchoolNetDaysDue?: number;
};

export type PaymentOptionsWithDiscount = {
  discountAmount?: number;
};

export type PaymentOptionsWithNonRefundablePortion = {
  /**
   * An optional value for a non-refundable application fee. If defined,
   * a new invoice cost is added for the fee.
   */
  nonRefundableAmount?: number;
};

export type PaymentOptionsWithChargesToSponsor = {
  /**
   * Number of days after invoicing ("net days") that a payment from Dreambound
   * to the school is due; e.g. for a Dreambound Agreement funded program. If
   * this is not set, the charges will not have a due date (i.e. they will never
   * be overdue).
   */
  chargesToSponsorNetDaysDue?: number;
};

export type SchoolPaymentOptionsWithProcessingFee = PaymentOptionsWithChargesToSchool & {
  /**
   * The percentage of what the customer pays that goes to Dreambound as a
   * processing fee. Enter as a whole percent; for example, for 3% enter '3'.
   */
  processingFeePercentage: number;
};

export type SchoolPaymentOptionsWithRevenueShare = PaymentOptionsWithChargesToSchool & {
  /**
   * The revenue share, or percentage of what the customer pays that goes to
   * Dreambound as a referral fee. Enter as a whole percent; for example,
   * for 20% enter '20'.
   */
  revenueSharePercentage: number | null;

  /**
   * The revenue share as a flat rate DollarAmountInCents value.
   * If both this and revenueSharePercentage, the total revenue share
   * is the sum of their dollar values.
   */
  revenueShareFlatRate: number | null;
};

export type SchoolPaymentOptionsWithCustomDescription = {
  /**
   * A custom description for the payment option. If defined, this will be
   * displayed on our customer pages as part of DreamSearch rather than falling through
   * to showing "No cost info".
   */
  description: string | null;
};

/**
 * Transformed values
 */

// Combination of all PaymentOptionConfig options
export type CombinedPaymentOptionConfig = SchoolEmployerSponsoredPaymentOptionConfig &
  SchoolNonPlatformPaymentOptionConfig &
  SchoolPayInFullPaymentOptionConfig &
  SchoolPaymentPlanPaymentOptionConfig &
  SchoolPayPerLeadPaymentOptionConfig &
  EmployerSponsoredSchoolPaymentOptionConfig;

// All PaymentOption types
export const PAYMENT_OPTION_TYPES = {
  ...SCHOOL_PAYMENT_OPTION_TYPES,
  ...SPONSOR_PAYMENT_OPTION_TYPES,
};

// All PaymentOption types (union)
export type PaymentOptionType = SchoolPaymentOptionType | SponsorPaymentOptionType;

/**
 * A map between PaymentOption types and values, which require an
 * implementation `T` for every PaymentOption type.
 *
 * For a map which does not require every PaymentOption to be implemented,
 * use `Partial<PaymentOptionMapType<T>` (but beware you might forget a
 * PaymentOption if you do this).
 */
export type PaymentOptionMapType<T> = {
  [Property in PaymentOptionType]: T;
};

export type SchoolPaymentOptionMapType<T> = {
  [Property in SchoolPaymentOptionType]: T;
};

export type SponsorPaymentOptionMapType<T> = {
  [Property in SponsorPaymentOptionType]: T;
};

// Don't delete this unused map-- it is used to remind you (with TS) that the
// `getPaymentOptionConfig` switcher above must handle each PaymentOptionType.
// After (and only after) you update the implementation above, update here.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const didWeDefineSwitcherCaseForAllPaymentOptionTypes: PaymentOptionMapType<boolean> = {
  EMPLOYER_SPONSORED_SCHOOL: true,
  SCHOOL_EMPLOYER_SPONSORED: true,
  SCHOOL_FINANCIAL_AID: true,
  SCHOOL_NON_PLATFORM: true,
  SCHOOL_PAY_IN_FULL: true,
  SCHOOL_PAY_PER_LEAD: true,
  SCHOOL_PAYMENT_PLAN: true,
};
